import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { Col, Row } from 'antd';
import * as Yup from 'yup';
import styled from 'styled-components';
import { FormInput, CustomInput } from 'atoms';
import InfoBanner from 'componentsv2/common/InfoBanner';
import RadioCardGroup from 'componentsv2/common/RadioCardGroup';
import {
	FormContainer,
	PreviousButton,
	SaveButton,
	CancelButton,
} from '../client/clientFormStyles';
import WarehouseSlabsField from './WarehouseSlabsField';
import {
	REVENUE_MODEL_COST_PLUS,
	REVENUE_MODEL_OPTIONS,
	REVENUE_MODEL_PER_ORDER,
	makeDefaultSlabs,
} from './warehouseRevenue';

const SectionLabel = styled.div`
	font-size: 15px;
	font-weight: 600;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	color: #8c8c8c;
	margin: 0 0 12px;
`;

const SectionBlock = styled.div`
	margin-bottom: 28px;

	&:last-of-type {
		margin-bottom: 8px;
	}
`;

const SectionDescription = styled.div`
	font-size: 13px;
	color: #8c8c8c;
	margin: -6px 0 12px;
`;

const HelperText = styled.div`
	font-size: 12px;
	color: #8c8c8c;
	margin-top: -12px;
	margin-bottom: 8px;
`;

const isBlank = (value) => value === '' || value === null || value === undefined;

/**
 * Cross-row slab rules. Mirrors the backend's service-layer validation (TDD §10:
 * "contiguous, non-overlapping, ascending") so users don't round-trip to a 422.
 */
const validateSlabSequence = (slabs) => {
	const issues = {};

	(slabs || []).forEach((slab, index) => {
		const min = Number(slab.slab_min);
		const max = Number(slab.slab_max);

		if (!isBlank(slab.slab_max) && !isBlank(slab.slab_min) && max <= min) {
			issues[index] = { ...issues[index], slab_max: 'Max must be greater than min' };
		}

		if (index === 0) return;

		const previous = slabs[index - 1];
		if (isBlank(previous.slab_max) || isBlank(slab.slab_min)) return;

		const expectedMin = Number(previous.slab_max) + 1;
		if (min < expectedMin) {
			issues[index] = { ...issues[index], slab_min: 'Slabs overlap' };
		} else if (min > expectedMin) {
			issues[index] = {
				...issues[index],
				slab_min: `Slab must start where the previous one ends (${expectedMin})`,
			};
		}
	});

	return issues;
};

const buildSchema = (requireReason) =>
	Yup.object().shape({
		model_type: Yup.string().required('Select a business model'),

		slabs: Yup.array().when('model_type', {
			is: REVENUE_MODEL_PER_ORDER,
			then: Yup.array()
				.min(1, 'Add at least one slab')
				.of(
					Yup.object().shape({
						slab_min: Yup.number()
							.typeError('Min must be a number')
							.min(0, 'Min cannot be negative')
							.required('Min is required'),
						slab_max: Yup.number()
							.typeError('Max must be a number')
							.min(0, 'Max cannot be negative')
							.required('Max is required'),
						rate_per_order: Yup.number()
							.typeError('Rate must be a number')
							.moreThan(0, 'Rate must be greater than 0')
							.required('Rate is required'),
					})
				),
			otherwise: Yup.array().notRequired(),
		}),

		cost_plus_margin_pct: Yup.number().when('model_type', {
			is: REVENUE_MODEL_COST_PLUS,
			then: Yup.number()
				.typeError('Margin % must be a number')
				.min(0, 'Margin % cannot be negative')
				.max(100, 'Margin % cannot exceed 100')
				.required('Margin % is required'),
			otherwise: Yup.number().notRequired(),
		}),

		// Optional: a project with no Cross Dock arrangement leaves this blank, and then
		// bag counts are not collected at all in the Warehousing module.
		// Required only when editing: creation records the terms as the starting point.
		reason: requireReason
			? Yup.string().trim().required('Enter a reason for this change')
			: Yup.string().notRequired(),

		cross_dock_rate: Yup.number()
			.typeError('Cross Dock rate must be a number')
			.moreThan(0, 'Cross Dock rate must be greater than 0')
			.nullable()
			.transform((value, original) => (original === '' ? null : value)),
	});

/**
 * Step 2 of project setup when Project Type = Warehousing — replaces Node Information.
 *
 * The revenue model is part of the project *create* payload and has no update endpoint
 * (backend TDD §5.2 / §8), so in edit mode this renders strictly read-only.
 */
export default function WarehouseInfoFormV2({
	warehouseData,
	setWarehouseData,
	setCurrentStep,
	isEditMode,
	isViewMode,
	onSubmit,
	onCancel,
	loading,
}) {
	// No update path for the revenue model — everything past creation is read-only
	const readOnly = isViewMode;
	// The revenue terms are editable after creation; the business model is not. Switching
	// per-order <-> cost-plus would reinterpret every figure already reported for the
	// project, so it stays as chosen at creation.
	const modelLocked = readOnly || isEditMode;

	const initialValues = useMemo(() => {
		const data = warehouseData || {};
		return {
			model_type: data.model_type || REVENUE_MODEL_PER_ORDER,
			slabs: data.slabs?.length ? data.slabs : makeDefaultSlabs(),
			cost_plus_margin_pct: data.cost_plus_margin_pct ?? '',
			// Already unwrapped from the API's charge list by the page — this is the
			// single rate, not the generic collection.
			cross_dock_rate: data.cross_dock_rate ?? '',
			reason: '',
		};
	}, [warehouseData]);

	const handleSubmit = (values, { setErrors: setFormErrors }) => {
		setWarehouseData(values);
		onSubmit(values, setFormErrors);
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={buildSchema(isEditMode && !isViewMode)}
			validate={(values) => {
				if (values.model_type !== REVENUE_MODEL_PER_ORDER) return {};
				const issues = validateSlabSequence(values.slabs);
				return Object.keys(issues).length ? { slabs: issues } : {};
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, submitForm }) => (
				<>
					<FormContainer>
						<Form>
							<SectionBlock>
								<SectionLabel>Business Model</SectionLabel>
								<RadioCardGroup
									ariaLabel="Business model"
									options={REVENUE_MODEL_OPTIONS}
									value={values.model_type}
									disabled={modelLocked}
									onChange={(value) => setFieldValue('model_type', value)}
								/>
							</SectionBlock>

							<SectionBlock>
								<SectionLabel>Revenue Part</SectionLabel>

								{values.model_type === REVENUE_MODEL_PER_ORDER ? (
									<>
										<SectionDescription>
											Define shipment-volume slabs and the rate per order (₹)
											for each slab.
										</SectionDescription>
										<WarehouseSlabsField isViewMode={readOnly} />
									</>
								) : (
									<Row gutter={24}>
										<Col xs={24} sm={12} lg={8}>
											<FormInput label="Margin %" name="cost_plus_margin_pct">
												<CustomInput
													name="cost_plus_margin_pct"
													type="number"
													min={0}
													max={100}
													step="any"
													suffix="%"
													placeholder="0"
													disabled={readOnly}
												/>
											</FormInput>
											<HelperText>
												Applied on top of total monthly cost to compute
												invoiceable revenue.
											</HelperText>
										</Col>
									</Row>
								)}
							</SectionBlock>

							<SectionBlock>
								<SectionLabel>Additional Charges</SectionLabel>
								<SectionDescription>
									Cross Dock is billed per bag on top of the base revenue model.
									One flat rate applies to a bag whatever it holds, so this is a
									single rate — leave it blank if the project has no Cross Dock
									arrangement.
								</SectionDescription>
								<Row gutter={24}>
									<Col xs={24} sm={12} lg={8}>
										<FormInput
											label="Cross Dock Rate (₹ per bag)"
											name="cross_dock_rate"
										>
											<CustomInput
												name="cross_dock_rate"
												type="number"
												min={0}
												step="any"
												prefix="₹"
												placeholder="0"
												disabled={readOnly}
											/>
										</FormInput>
										<HelperText>
											Bag Count × this rate is added to MTD revenue each day.
										</HelperText>
									</Col>
								</Row>
							</SectionBlock>

							{isEditMode && !isViewMode && (
								<SectionBlock>
									<SectionLabel>Reason for change</SectionLabel>
									<SectionDescription>
										Revenue terms are not versioned — an edit also changes
										months already reported on, so the reason is recorded in the
										project&apos;s revenue edit log.
									</SectionDescription>
									<Row gutter={24}>
										<Col xs={24} lg={16}>
											<FormInput label="Reason" name="reason">
												<CustomInput
													name="reason"
													placeholder="Why are these terms changing?"
													maxLength={500}
												/>
											</FormInput>
										</Col>
									</Row>
								</SectionBlock>
							)}

							<InfoBanner tone="neutral">
								Cost heads are no longer captured here. Fixed costs are defined and
								edited on the Fixed tab of the project inside the Warehousing
								module, alongside daily and month-end costs.
							</InfoBanner>
						</Form>
					</FormContainer>

					{/* Navigation Buttons - Outside the white box */}
					<Row justify="end" style={{ marginTop: 24, gap: 16 }}>
						<PreviousButton
							type="button"
							onClick={() => {
								// Lift what's been entered so far, otherwise coming back to
								// this step remounts the form from defaults and the slabs /
								// margin the user typed are lost.
								setWarehouseData(values);
								setCurrentStep(0);
							}}
						>
							Back
						</PreviousButton>

						{isEditMode && !isViewMode && (
							<CancelButton onClick={onCancel}>Cancel</CancelButton>
						)}

						{!readOnly && (
							<SaveButton
								type="button"
								loading={loading}
								disabled={loading}
								onClick={submitForm}
							>
								{isEditMode ? 'Save Warehouse Information' : 'Save Project'}
							</SaveButton>
						)}
					</Row>
				</>
			)}
		</Formik>
	);
}

WarehouseInfoFormV2.propTypes = {
	warehouseData: PropTypes.object,
	setWarehouseData: PropTypes.func.isRequired,
	setCurrentStep: PropTypes.func.isRequired,
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func,
	loading: PropTypes.bool,
};

WarehouseInfoFormV2.defaultProps = {
	warehouseData: {},
	isEditMode: false,
	isViewMode: false,
	onCancel: () => {},
	loading: false,
};
